import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();
  const effectiveDate = "March 18, 2026";
  const companyName = "TerryLBPrompts";
  const websiteUrl = "terrylbprompts.com";
  const contactEmail = "support@terrylbprompts.com";

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Terms of Service</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="prose prose-gray max-w-none">
          <p className="text-gray-500 mb-8">Effective Date: {effectiveDate}</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">1. Acceptance of Terms</h2>
            <p>
              Welcome to {companyName} and DailyDrop AI ("Service"). By accessing or using our website at {websiteUrl}, 
              mobile applications, or any related services, you agree to be bound by these Terms of Service ("Terms"). 
              If you do not agree to these Terms, please do not use our Service.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">2. Description of Service</h2>
            <p>
              {companyName} provides AI-powered tools for content creation, including but not limited to:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>AI image generation and manipulation</li>
              <li>Prompt building and learning tools</li>
              <li>DailyDrop AI - social media content creation and scheduling</li>
              <li>AI-powered caption, hashtag, and content generation</li>
              <li>Multi-platform content distribution tools</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">3. User Accounts</h2>
            <p>
              To access certain features, you must create an account. You agree to:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Provide accurate and complete registration information</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
            </ul>
            <p className="mt-2">
              You must be at least 13 years old to use this Service. If you are under 18, you must have 
              parental or guardian consent.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">4. Subscription and Payment</h2>
            <p>
              Some features require a paid subscription. By subscribing, you agree to:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>Pay all applicable fees as described at the time of purchase</li>
              <li>Provide valid payment information</li>
              <li>Accept automatic renewal unless you cancel before the renewal date</li>
            </ul>
            <p className="mt-2">
              Refunds are handled on a case-by-case basis. Contact {contactEmail} for refund requests.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">5. User Content and Conduct</h2>
            <p>You retain ownership of content you create using our Service. However, you agree not to:</p>
            <ul className="list-disc pl-6 mt-2">
              <li>Create content that is illegal, harmful, threatening, abusive, or harassing</li>
              <li>Generate content that infringes on intellectual property rights</li>
              <li>Use the Service for spam, phishing, or malicious activities</li>
              <li>Attempt to reverse engineer or hack the Service</li>
              <li>Use automated systems or bots without authorization</li>
              <li>Resell or redistribute AI-generated content for commercial gain without proper licensing</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">6. Social Media Platform Integration</h2>
            <p>
              Our DailyDrop AI feature allows you to connect third-party social media accounts. By connecting these accounts:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>You authorize us to access and post content on your behalf</li>
              <li>You remain responsible for content posted to your social accounts</li>
              <li>You agree to comply with each platform's terms of service</li>
              <li>You can disconnect your accounts at any time through Settings</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">7. AI-Generated Content</h2>
            <p>
              Content generated using our AI tools:
            </p>
            <ul className="list-disc pl-6 mt-2">
              <li>May not be unique and similar content may be generated for other users</li>
              <li>Should be reviewed before publishing to ensure accuracy and appropriateness</li>
              <li>Is provided "as is" without guarantees of accuracy or suitability</li>
              <li>May be subject to content moderation and filtering</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">8. Intellectual Property</h2>
            <p>
              The Service, including its design, features, and content (excluding user-generated content), 
              is owned by {companyName} and protected by intellectual property laws. You may not copy, 
              modify, or distribute our proprietary content without permission.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, {companyName} shall not be liable for any indirect, 
              incidental, special, consequential, or punitive damages resulting from your use of the Service. 
              Our total liability shall not exceed the amount you paid us in the 12 months preceding the claim.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">10. Disclaimer of Warranties</h2>
            <p>
              The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either 
              express or implied, including but not limited to implied warranties of merchantability, 
              fitness for a particular purpose, or non-infringement.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">11. Termination</h2>
            <p>
              We reserve the right to suspend or terminate your account at any time for violation of 
              these Terms or for any other reason at our discretion. Upon termination, your right to 
              use the Service ceases immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">12. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. We will notify you of significant changes 
              by posting the new Terms on this page and updating the "Effective Date." Your continued 
              use of the Service after changes constitutes acceptance of the modified Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">13. Governing Law</h2>
            <p>
              These Terms shall be governed by and construed in accordance with the laws of the 
              United States, without regard to conflict of law principles.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold mb-4">14. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p className="mt-2">
              <strong>Email:</strong> {contactEmail}<br />
              <strong>Website:</strong> {websiteUrl}
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center text-gray-500 text-sm">
          <p>© 2026 {companyName}. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="/privacy" className="hover:text-gray-700">Privacy Policy</a>
            <span>•</span>
            <a href="/terms" className="hover:text-gray-700">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
